import { GameState, createDefaultState, BuffState, WrinklerState } from './GameState';
import { bn } from './BigNumber';
import { eventBus } from './EventBus';

const SAVE_KEY = 'CookieClickerV2';
const CURRENT_VERSION = 1;
const AUTOSAVE_INTERVAL = 60_000;

interface SerializedState {
  version: number;
  timestamp: number;
  data: Record<string, any>;
}

type Migration = (data: Record<string, any>) => Record<string, any>;

const MIGRATIONS: Record<number, Migration> = {};

export class SaveManager {
  private autosaveTimer: ReturnType<typeof setInterval> | null = null;

  startAutosave(getState: () => GameState, onAfterSave?: (state: GameState) => void): void {
    this.stopAutosave();
    this.autosaveTimer = setInterval(() => {
      const state = getState();
      this.save(state);
      onAfterSave?.(state);
      eventBus.emit('game:autosaved');
    }, AUTOSAVE_INTERVAL);
  }

  stopAutosave(): void {
    if (this.autosaveTimer !== null) {
      clearInterval(this.autosaveTimer);
      this.autosaveTimer = null;
    }
  }

  save(state: GameState): boolean {
    try {
      const serialized = this.serialize(state);
      const json = JSON.stringify(serialized);
      const encoded = btoa(unescape(encodeURIComponent(json)));
      localStorage.setItem(SAVE_KEY, encoded);
      return true;
    } catch (e) {
      console.error('[SaveManager] Save failed:', e);
      return false;
    }
  }

  load(): GameState | null {
    try {
      const raw = localStorage.getItem(SAVE_KEY);
      if (!raw) return null;
      const json = decodeURIComponent(escape(atob(raw)));
      const serialized: SerializedState = JSON.parse(json);
      return this.deserialize(this.migrate(serialized));
    } catch (e) {
      console.error('[SaveManager] Load failed:', e);
      return null;
    }
  }

  exportSave(state: GameState): string {
    const serialized = this.serialize(state);
    return btoa(unescape(encodeURIComponent(JSON.stringify(serialized))));
  }

  importSave(encoded: string): GameState | null {
    try {
      const json = decodeURIComponent(escape(atob(encoded)));
      const serialized: SerializedState = JSON.parse(json);
      return this.deserialize(this.migrate(serialized));
    } catch (e) {
      console.error('[SaveManager] Import failed:', e);
      return null;
    }
  }

  wipeSave(): void {
    localStorage.removeItem(SAVE_KEY);
  }

  private migrate(data: SerializedState): SerializedState {
    let { version } = data;
    while (version < CURRENT_VERSION) {
      const migration = MIGRATIONS[version + 1];
      if (!migration) break;
      data.data = migration(data.data);
      version++;
    }
    data.version = CURRENT_VERSION;
    return data;
  }

  private serialize(state: GameState): SerializedState {
    const buildings: Record<string, any> = {};
    state.buildings.forEach((b, key) => {
      buildings[key] = {
        amount: b.amount,
        bought: b.bought,
        totalCookies: b.totalCookies.toString(),
        level: b.level,
        free: b.free,
        minigameSave: b.minigameSave,
      };
    });

    const upgrades: Record<string, { unlocked: boolean; bought: boolean }> = {};
    state.upgrades.forEach((u, key) => {
      upgrades[key] = { unlocked: u.unlocked, bought: u.bought };
    });

    const buffs: Record<string, any> = {};
    state.buffs.forEach((b, key) => {
      buffs[key] = { ...b };
    });

    return {
      version: CURRENT_VERSION,
      timestamp: Date.now(),
      data: {
        cookies: state.cookies.toString(),
        cookiesEarned: state.cookiesEarned.toString(),
        cookieClicks: state.cookieClicks,
        startDate: state.startDate,
        lastSaveDate: Date.now(),
        sessionStartDate: state.sessionStartDate,
        buildings,
        upgrades,
        achievements: [...state.achievements],
        buffs,
        prestige: {
          level: state.prestige.level.toString(),
          heavenlyChips: state.prestige.heavenlyChips.toString(),
          heavenlyChipsSpent: state.prestige.heavenlyChipsSpent.toString(),
          heavenlyUpgrades: [...state.prestige.heavenlyUpgrades],
          ascensionMode: state.prestige.ascensionMode,
        },
        dragon: { ...state.dragon },
        wrinklers: state.wrinklers.map((w) => ({
          ...w,
          sucked: w.sucked.toString(),
        })),
        elderWrath: state.elderWrath,
        pledgeT: state.pledgeT,
        season: state.season,
        seasonT: state.seasonT,
        lumps: state.lumps,
        lumpT: state.lumpT,
        lumpRefill: state.lumpRefill,
        lumpCurrentType: state.lumpCurrentType,
        seed: state.seed,
        bakeryName: state.bakeryName,
        prefs: { ...state.prefs },
      },
    };
  }

  private deserialize(saved: SerializedState): GameState {
    const d = saved.data;
    const state = createDefaultState();

    state.cookies = bn(d.cookies ?? 0);
    state.cookiesEarned = bn(d.cookiesEarned ?? 0);
    state.cookieClicks = d.cookieClicks ?? 0;
    state.startDate = d.startDate ?? Date.now();
    state.lastSaveDate = d.lastSaveDate ?? Date.now();
    state.sessionStartDate = Date.now();

    if (d.buildings) {
      for (const [key, b] of Object.entries<any>(d.buildings)) {
        state.buildings.set(key, {
          amount: b.amount,
          bought: b.bought,
          totalCookies: bn(b.totalCookies ?? 0),
          level: b.level ?? 0,
          free: b.free ?? 0,
          minigameSave: b.minigameSave ?? '',
        });
      }
    }

    if (d.upgrades) {
      for (const [key, u] of Object.entries<any>(d.upgrades)) {
        state.upgrades.set(key, { unlocked: u.unlocked, bought: u.bought });
      }
    }

    if (d.achievements) {
      for (const a of d.achievements) state.achievements.add(a);
    }

    if (d.buffs) {
      for (const [key, b] of Object.entries<any>(d.buffs)) {
        state.buffs.set(key, b as BuffState);
      }
    }

    if (d.prestige) {
      state.prestige = {
        level: bn(d.prestige.level ?? 0),
        heavenlyChips: bn(d.prestige.heavenlyChips ?? 0),
        heavenlyChipsSpent: bn(d.prestige.heavenlyChipsSpent ?? 0),
        heavenlyUpgrades: new Set(d.prestige.heavenlyUpgrades ?? []),
        ascensionMode: d.prestige.ascensionMode ?? 0,
      };
    }

    if (d.dragon) state.dragon = { ...d.dragon };

    if (d.wrinklers) {
      state.wrinklers = d.wrinklers.map((w: any) => ({
        ...w,
        sucked: bn(w.sucked ?? 0),
      } as WrinklerState));
    }

    state.elderWrath = d.elderWrath ?? 0;
    state.pledgeT = d.pledgeT ?? 0;
    state.season = d.season ?? '';
    state.seasonT = d.seasonT ?? 0;
    state.lumps = d.lumps ?? -1;
    state.lumpT = d.lumpT ?? 0;
    state.lumpRefill = d.lumpRefill ?? 0;
    state.lumpCurrentType = d.lumpCurrentType ?? 0;
    state.seed = d.seed ?? state.seed;
    state.bakeryName = d.bakeryName ?? 'My bakery';
    if (d.prefs) state.prefs = { ...state.prefs, ...d.prefs };

    return state;
  }
}
